import { useState, useEffect, useCallback, useRef } from 'react';
import { getOarsExercises, getOarsExercise, generateOarsExercise } from '../services/api';

const OARS_BANK_CATEGORIES = new Set(['OQ', 'SR', 'CR', 'AF', 'S']);
const RANDOM_OARS_CATEGORIES = ['OQ', 'AF', 'SR', 'CR', 'S'];
const RECENT_GROUP_WINDOW = 3;

const toOarsInfoTab = (category) => {
  if (category === 'OQ') return 'OQ';
  if (category === 'AF') return 'A';
  if (category === 'S') return 'S';
  return category ?? 'random';
};

const chooseRandomOarsCategory = () => {
  const index = Math.floor(Math.random() * RANDOM_OARS_CATEGORIES.length);
  return RANDOM_OARS_CATEGORIES[index];
};

function useOarsFlow({
  selectedProfile,
  authenticated,
  openFeedbackDialog,
}) {
  const recentGroupIdsRef = useRef({ OQ: [], SR: [], CR: [], AF: [], S: [] });
  const [oarsExercise, setOarsExercise] = useState(null);
  const [oarsExerciseLoading, setOarsExerciseLoading] = useState(false);
  const [oarsAttempt, setOarsAttempt] = useState(null);
  const [oarsAnalyzing, setOarsAnalyzing] = useState(false);
  const [oarsStreamingEvaluation, setOarsStreamingEvaluation] = useState(null);
  const [oarsExerciseList, setOarsExerciseList] = useState([]);
  const [oarsResetKey, setOarsResetKey] = useState(0);
  const [oarsFilter, setOarsFilter] = useState(undefined);
  const [oarsExerciseIndex, setOarsExerciseIndex] = useState(0);
  const [specificSkillsAttemptCount, setSpecificSkillsAttemptCount] = useState(0);
  const [specificSkillsRetryAttemptId, setSpecificSkillsRetryAttemptId] = useState(null);
  const [specificSkillsFeedbackSeen, setSpecificSkillsFeedbackSeen] = useState(false);
  const [oarsPopupBlocking, setOarsPopupBlocking] = useState(false);
  const [oarsInfoOpen, setOarsInfoOpen] = useState(false);
  const [oarsInfoTab, setOarsInfoTab] = useState('overview');
  const [oarsDismissSkillPopups, setOarsDismissSkillPopups] = useState(false);
  const [oarsInfoShowCheckbox, setOarsInfoShowCheckbox] = useState(false);

  const oarsEffectivelyBlocking = (oarsInfoOpen && oarsInfoShowCheckbox) || oarsPopupBlocking;
  const oarsExerciseTotal = !oarsFilter || OARS_BANK_CATEGORIES.has(oarsFilter)
    ? 0
    : oarsFilter
    ? oarsExerciseList.filter((exercise) => exercise.oars_category === oarsFilter).length
    : oarsExerciseList.length;

  const handleOarsInfoOpen = useCallback((tab = 'overview') => {
    setOarsInfoTab(toOarsInfoTab(tab));
    setOarsInfoOpen((wasOpen) => {
      if (!wasOpen) {
        setOarsInfoShowCheckbox(false);
      }
      return true;
    });
  }, []);

  const resetOarsState = useCallback(() => {
    setOarsExercise(null);
    setOarsExerciseLoading(false);
    setOarsAttempt(null);
    setOarsStreamingEvaluation(null);
    setOarsExerciseList([]);
    setOarsResetKey(0);
    setOarsFilter(undefined);
    setOarsExerciseIndex(0);
    setSpecificSkillsAttemptCount(0);
    setSpecificSkillsRetryAttemptId(null);
  }, []);

  useEffect(() => {
    if (selectedProfile === -3 && oarsExerciseList.length === 0 && !oarsExercise && authenticated) {
      (async () => {
        try {
          const data = await getOarsExercises();
          setOarsExerciseList(data.exercises || []);
        } catch (err) {
          console.error('Failed to load OARS exercises on init:', err);
        }
      })();
    }
  }, [selectedProfile, authenticated, oarsExerciseList.length, oarsExercise]);

  const handleOarsTryAgain = useCallback(() => {
    setOarsAttempt(null);
    setOarsStreamingEvaluation(null);
    setOarsResetKey((key) => key + 1);
  }, []);

  const handleOarsReplayAttempt = useCallback((exercise, attempt) => {
    setOarsExercise(exercise);
    setOarsAttempt(attempt);
    setOarsStreamingEvaluation(null);
    setOarsAnalyzing(false);
    setOarsExerciseLoading(false);
    setOarsPopupBlocking(false);
    setOarsInfoOpen(false);
    setOarsInfoShowCheckbox(false);
    setOarsFilter(exercise?.oars_category);
    setOarsExerciseIndex(0);
    setSpecificSkillsRetryAttemptId(attempt?.attempt_id || attempt?.id || null);
    setOarsResetKey((key) => key + 1);
  }, []);

  const handleOarsLoadExercise = useCallback((exercise) => {
    setOarsExercise(exercise);
    setOarsAttempt(null);
    setOarsStreamingEvaluation(null);
    setOarsAnalyzing(false);
    setOarsExerciseLoading(false);
    setOarsPopupBlocking(false);
    setOarsInfoOpen(false);
    setOarsInfoShowCheckbox(false);
    setOarsFilter(exercise?.oars_category);
    setOarsExerciseIndex(0);
    setSpecificSkillsRetryAttemptId(null);
    setOarsResetKey((key) => key + 1);
  }, []);

  const getRecentGroupIds = useCallback((category) => {
    if (!OARS_BANK_CATEGORIES.has(category)) {
      return [];
    }
    return [...(recentGroupIdsRef.current[category] || [])];
  }, []);

  const recordRecentGroupId = useCallback((category, exercise) => {
    if (!OARS_BANK_CATEGORIES.has(category)) {
      return;
    }
    const groupId = exercise?.group_id;
    if (!groupId) {
      return;
    }
    const current = recentGroupIdsRef.current[category] || [];
    const next = [groupId, ...current.filter((value) => value !== groupId)].slice(0, RECENT_GROUP_WINDOW);
    recentGroupIdsRef.current[category] = next;
  }, []);

  const loadBankExercise = useCallback(async (category) => {
    setOarsExerciseLoading(true);
    setOarsExercise(null);
    try {
      const loaded = await generateOarsExercise(category, {
        recent_group_ids: getRecentGroupIds(category),
      });
      const exercise = {
        ...loaded.exercise,
        draft_signature: loaded.exercise_signature || null,
      };
      recordRecentGroupId(category, exercise);
      return exercise;
    } finally {
      setOarsExerciseLoading(false);
    }
  }, [getRecentGroupIds, recordRecentGroupId]);

  const handleOarsNextExercise = useCallback(async () => {
    setOarsAttempt(null);
    setOarsStreamingEvaluation(null);
    setOarsResetKey((key) => key + 1);
    setSpecificSkillsRetryAttemptId(null);
    try {
      if (!oarsFilter || OARS_BANK_CATEGORIES.has(oarsFilter)) {
        const category = oarsFilter || chooseRandomOarsCategory();
        const loaded = await loadBankExercise(category);
        setOarsExerciseIndex(0);
        setOarsExercise(loaded);
        return;
      }
      if (oarsExerciseList.length > 0) {
        const pool = (oarsFilter
          ? oarsExerciseList.filter((exercise) => exercise.oars_category === oarsFilter)
          : oarsExerciseList).slice().sort((a, b) => a.id.localeCompare(b.id));
        const nextIndex = (oarsExerciseIndex + 1) % pool.length;
        setOarsExerciseIndex(nextIndex);
        const full = await getOarsExercise(pool[nextIndex].id);
        setOarsExercise(full);
      }
    } catch (err) {
      console.error('Failed to load next OARS exercise:', err);
    }
  }, [loadBankExercise, oarsExerciseList, oarsExerciseIndex, oarsFilter]);

  const handleOarsFilterChange = useCallback(async (category) => {
    setOarsFilter(category);
    setOarsAttempt(null);
    setOarsStreamingEvaluation(null);
    setOarsResetKey((key) => key + 1);
    setSpecificSkillsRetryAttemptId(null);
    if (!oarsDismissSkillPopups) {
      setOarsInfoTab(toOarsInfoTab(category));
      setOarsInfoShowCheckbox(true);
      setOarsInfoOpen(true);
    }
    try {
      if (!category || OARS_BANK_CATEGORIES.has(category)) {
        const bankCategory = category || chooseRandomOarsCategory();
        const loaded = await loadBankExercise(bankCategory);
        setOarsExerciseIndex(0);
        setOarsExercise(loaded);
      } else {
        let list = oarsExerciseList;
        if (list.length === 0) {
          const data = await getOarsExercises();
          list = data.exercises || [];
          setOarsExerciseList(list);
        }
        const pool = (category
          ? list.filter((exercise) => exercise.oars_category === category)
          : list).slice().sort((a, b) => a.id.localeCompare(b.id));
        if (pool.length > 0) {
          setOarsExerciseIndex(0);
          const full = await getOarsExercise(pool[0].id);
          setOarsExercise(full);
        }
      }
    } catch (err) {
      console.error('Failed to load filtered OARS exercise:', err);
    }
    return true;
  }, [loadBankExercise, oarsDismissSkillPopups, oarsExerciseList]);

  const handleOarsAttempt = useCallback((attempt) => {
    setOarsAttempt(attempt);
    setOarsStreamingEvaluation(null);
    setSpecificSkillsRetryAttemptId(attempt?.attempt_id || null);
    setSpecificSkillsAttemptCount((prevCount) => {
      const nextCount = prevCount + 1;
      if (nextCount >= 3 && !specificSkillsFeedbackSeen) {
        setSpecificSkillsFeedbackSeen(true);
        openFeedbackDialog('specific-skills');
      }
      return nextCount;
    });
  }, [openFeedbackDialog, specificSkillsFeedbackSeen]);

  return {
    oarsExercise,
    oarsExerciseLoading,
    oarsAttempt,
    oarsAnalyzing,
    setOarsAnalyzing,
    oarsStreamingEvaluation,
    setOarsStreamingEvaluation,
    oarsExerciseList,
    oarsResetKey,
    oarsFilter,
    oarsExerciseIndex,
    specificSkillsAttemptCount,
    specificSkillsRetryAttemptId,
    oarsPopupBlocking,
    setOarsPopupBlocking,
    oarsInfoOpen,
    setOarsInfoOpen,
    oarsInfoTab,
    oarsDismissSkillPopups,
    setOarsDismissSkillPopups,
    oarsInfoShowCheckbox,
    setOarsInfoShowCheckbox,
    oarsEffectivelyBlocking,
    oarsExerciseTotal,
    handleOarsInfoOpen,
    handleOarsTryAgain,
    handleOarsReplayAttempt,
    handleOarsLoadExercise,
    handleOarsNextExercise,
    handleOarsFilterChange,
    handleOarsAttempt,
    resetOarsState,
  };
}

export default useOarsFlow;
